import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CreditCard, Lock } from 'lucide-react';

const Checkout = () => {
  const { cart, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    address: '',
    city: '',
    zipCode: '',
    country: ''
  });
  const [processing, setProcessing] = useState(false);

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);

    // TODO: Implement actual payment processing
    setTimeout(() => {
      setProcessing(false);
      clearCart();
      alert('Payment successful! Check your email for download links.');
      navigate('/dashboard');
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      <div className="pt-24 px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-8">Checkout</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Checkout Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Contact Information */}
                <Card className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 border-white/10">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-white mb-4">Contact Information</h3>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="email" className="text-white">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="bg-black/50 border-white/10 text-white"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="name" className="text-white">Full Name</Label>
                        <Input
                          id="name"
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="bg-black/50 border-white/10 text-white"
                          required
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Billing Address */}
                <Card className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 border-white/10">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-white mb-4">Billing Address</h3>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="address" className="text-white">Address</Label>
                        <Input
                          id="address"
                          type="text"
                          value={formData.address}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                          className="bg-black/50 border-white/10 text-white"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="city" className="text-white">City</Label>
                          <Input
                            id="city"
                            type="text"
                            value={formData.city}
                            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                            className="bg-black/50 border-white/10 text-white"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="zipCode" className="text-white">ZIP Code</Label>
                          <Input
                            id="zipCode"
                            type="text"
                            value={formData.zipCode}
                            onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                            className="bg-black/50 border-white/10 text-white"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="country" className="text-white">Country</Label>
                        <Input
                          id="country"
                          type="text"
                          value={formData.country}
                          onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                          className="bg-black/50 border-white/10 text-white"
                          required
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Payment Method */}
                <Card className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 border-white/10">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-white mb-4">Payment Method</h3>
                    <div className="space-y-3">
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('stripe')}
                        className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                          paymentMethod === 'stripe'
                            ? 'border-purple-500 bg-purple-600/20'
                            : 'border-white/10 hover:border-white/20'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <CreditCard className="text-purple-400 mr-3" size={24} />
                            <div>
                              <span className="text-white font-medium block">Credit/Debit Card</span>
                              <span className="text-gray-400 text-sm">Powered by Stripe</span>
                            </div>
                          </div>
                          {paymentMethod === 'stripe' && (
                            <span className="text-purple-400 text-2xl">✓</span>
                          )}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('paypal')}
                        className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                          paymentMethod === 'paypal'
                            ? 'border-purple-500 bg-purple-600/20'
                            : 'border-white/10 hover:border-white/20'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-6 h-6 bg-blue-500 rounded mr-3 flex items-center justify-center text-white font-bold text-xs">
                              P
                            </div>
                            <div>
                              <span className="text-white font-medium block">PayPal</span>
                              <span className="text-gray-400 text-sm">Fast & secure</span>
                            </div>
                          </div>
                          {paymentMethod === 'paypal' && (
                            <span className="text-purple-400 text-2xl">✓</span>
                          )}
                        </div>
                      </button>
                    </div>
                  </CardContent>
                </Card>

                <Button
                  type="submit"
                  disabled={processing}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-lg flex items-center justify-center"
                >
                  {processing ? (
                    <span>Processing...</span>
                  ) : (
                    <>
                      <Lock className="mr-2" size={20} />
                      Complete Purchase - ${getCartTotal().toFixed(2)}
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 border-white/10 sticky top-24">
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-6">Order Summary</h3>
                  <div className="space-y-4 mb-6">
                    {cart.map((item) => (
                      <div key={item.id} className="flex justify-between text-sm">
                        <span className="text-gray-400">
                          {item.name}{item.game} x{item.quantity}
                        </span>
                        <span className="text-white">${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="border-t border-white/10 pt-4">
                      <div className="flex justify-between text-gray-400 mb-2">
                        <span>Subtotal</span>
                        <span className="text-white">${getCartTotal().toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-gray-400 mb-4">
                        <span>Tax</span>
                        <span className="text-white">$0.00</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xl font-bold text-white">Total</span>
                        <span className="text-2xl font-bold text-purple-400">
                          ${getCartTotal().toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-black/30 rounded-lg p-4">
                    <p className="text-gray-400 text-sm">
                      <Lock className="inline mr-1" size={14} />
                      Your payment is secured with 256-bit SSL encryption
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Checkout;